import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, Input, InputLabel, MenuItem, Select, TextField, useTheme } from "@material-ui/core";
import ProductList from "../../../Common/ProductList/ProductList";
import { ProductListChangeEvent } from "../../../Common/ProductList/utils";
import StartGroupSelector from "../../../Common/StartGroupSelector";
import { ApiBackend } from "../../../../providers/apibackend";
import { Product } from "../../../../model/Product";
import { vasaloppetTimeGetterFormatter } from "../../../../utilities/date";
import SelectPointOfSale from "../../../Orders/SelectPointOfSale";
import { ProductVariantPriceGroup } from "../../../../model/ProductVariantPriceGroup";
import { initNewOrder, Order, OrderMetadata } from "../../../../model/Order";
import { OrderItem } from "../../../../model/OrderItem";
import { OrderContact } from "../../../../model/OrderContact";
import { newEmptyEntry } from "../../../../model/Entry";
import { Alert } from "@material-ui/lab";

type CreateReservationDialogProps = {
    companyId: string;
    onSave: () => void;
    onAbort: () => void;
};

type InitValue = {
    b2bFakturaPos: string;
};

const CreateReservationDialog = (props: CreateReservationDialogProps) => {
    const [initValue, setInitValue] = useState<InitValue | null>(null);
    const [raceProduct, setRaceProduct] = useState<Product | null>(null);

    const [product, setProduct] = useState<string | null>(null);
    const [variant, setVariant] = useState<string | null>(null);
    const [startGroup, setStartGroup] = useState<string | null>(null);
    const [priceGroup, setPriceGroup] = useState<ProductVariantPriceGroup | null>(null);
    const [numItems, setNumItems] = useState(1);
    const [reservationName, setReservationName] = useState<string>("");
    const [pointOfSale, setPointOfSale] = useState<string | null>(null);
    const [invoiceLater, setInvoiceLater] = useState(false);
    const [invoiceDate, setInvoiceDate] = useState<string | null>(null);
    const [hasError, setHasError] = useState(false);
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);
    const [isCreateComplete, setIsCreateComplete] = useState(false);

    const theme = useTheme();
    const initialized = useRef<boolean>(false);
    const api = new ApiBackend();

    // Run on load
    useEffect(() => {

        init();
    }, []);

    useEffect(() => {
        if (!hasError) {
            return;
        }

        // Reset error when form changes
        setHasError(false);
    }, [product, variant, startGroup, priceGroup, numItems, reservationName, pointOfSale, invoiceLater, invoiceDate]);

    const init = async () => {
        const nextValue = await api.getB2bFakturaPointOfSale();
        setPointOfSale(nextValue.id);
        setInitValue({ b2bFakturaPos: nextValue.id });
        initialized.current = true;
    };

    const handleCreate = async () => {
        const { companyId } = props;

        setIsCreatingOrder(true);

        try {
            const res = await api.createCompanyReservation(companyId, pointOfSale, getCreateOrder());
            if (!res) {
                throw new Error();
            }

            setIsCreateComplete(true);
        } catch (err) {
            setHasError(true);
        } finally {
            setIsCreatingOrder(false);
        }
    };

    const getCreateOrder = (): Order => {
        const { companyId } = props;
        const order = initNewOrder();

        order.locale = "se";
        order.b2bId = companyId;

        order.contact = {
            firstName: "Reservering via Kontrollen"
        } as OrderContact;

        const orderMetadata: OrderMetadata[] = [{
            key: "reservation_name",
            value: reservationName
        }];

        if (pointOfSale === initValue?.b2bFakturaPos && invoiceLater) {
            orderMetadata.push({
                key: "invoice_date",
                value: invoiceDate
            });
        }

        order.metadata = orderMetadata;

        let orderItems: OrderItem[] = [];

        const itemMetaDataItems: { key: string; value: any }[] = [
            { key: "b2b_id", value: companyId },
        ];

        for (let i = 0; i < numItems; i++) {
            const entry = newEmptyEntry();

            if (!!startGroup) {
                entry.startgroup = startGroup;
            }

            orderItems.push({
                productId: product,
                productVariantId: variant,
                productVariantPriceGroupId: priceGroup.id,
                numberOfProducts: 1,
                entry: entry,
                metadata: itemMetaDataItems,
            } as OrderItem);
        }

        order.items = orderItems;

        return order;
    };

    const handleNumItemsChange = (event: React.ChangeEvent<{ value: string }>): void => {
        if (!initialized.current) {
            return;
        }

        setNumItems(+event.target.value);
    };

    const handleProductChange = async (evt: ProductListChangeEvent) => {
        if (!initialized.current) {
            return;
        }

        const nextValue = evt.selectedIds[0];

        setRaceProduct(null);
        setVariant(null);
        setStartGroup(null);
        setPriceGroup(null);
        setProduct(nextValue);

        const nextProduct = await api.getProduct(nextValue);
        setRaceProduct(nextProduct);
    };

    const handleVariantChange = (evt: React.ChangeEvent<{ value: string }>) => {
        if (!initialized.current) {
            return;
        }

        setStartGroup(null);
        const nextValue = evt.target.value;
        setVariant(nextValue);
    }

    const handlePosChange = (nextValue: string) => {
        setInvoiceLater(false);
        setInvoiceDate(null);
        setPointOfSale(nextValue);
    };

    const handleInvoiceLaterChange = (e: React.ChangeEvent, nextValue: boolean) => {
        setInvoiceDate(null);
        setInvoiceLater(nextValue);
    };

    const handleInvoiceDate = (e: React.ChangeEvent<{ value: string }>) => {
        setInvoiceDate(e.target.value);
    };

    const isFormValid = () => {
        if (!product || !variant || !numItems || !reservationName || !priceGroup || !pointOfSale) {
            return false;
        }

        if (pointOfSale === initValue?.b2bFakturaPos && invoiceLater && !invoiceDate) {
            return false;
        }

        return true;
    };

    const render = () => {
        const { onAbort, onSave } = props;

        const isSaveDisabled = !isFormValid() || isCreatingOrder;
        const price = priceGroup && numItems ? `Totalt pris: ${priceGroup.price * numItems} kr` : "";

        return (
            <Dialog
                open={true}
                disableEscapeKeyDown
                fullWidth={true}
                maxWidth="md"
            >
                <DialogTitle>
                    Reservera platser i lopp
                </DialogTitle>
                <DialogContent dividers>
                    {renderContent()}
                    {hasError &&
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Alert severity="error">Någonting gick fel när reservation skulle skapas</Alert>
                            </Grid>
                        </Grid>
                    }
                    {isCreateComplete &&
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Alert severity="info">Reservation skapad. Du kan nu stänga dialogen.</Alert>
                            </Grid>
                        </Grid>
                    }
                </DialogContent>
                <DialogActions>
                    <Box style={{ display: "flex", lineHeight: "37px", width: "100%" }}>
                        <Box style={{ flex: 1 }}>
                            {isCreatingOrder && <>
                                <CircularProgress color="secondary" />
                            </>}
                        </Box>
                        <Box style={{ width: 200 }}>
                            {price}
                        </Box>
                        <Box style={{ display: "flex", flex: 1, gap: 8, justifyContent: "flex-end" }}>
                            {isCreateComplete ?
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    disabled={isCreatingOrder}
                                    onClick={() => {
                                        onSave();
                                    }}
                                >
                                    Stäng
                                </Button>
                                :
                                <>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        disabled={isSaveDisabled}
                                        onClick={handleCreate}
                                    >
                                        Skapa reservation
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        disabled={isCreatingOrder}
                                        onClick={() => {
                                            onAbort();
                                        }}
                                    >
                                        Avbryt
                                    </Button>
                                </>
                            }
                        </Box>
                    </Box>
                </DialogActions>
            </Dialog>
        );
    };

    const renderContent = () => {
        if (!initValue) {
            return null;
        }

        const selectedVariant = raceProduct?.variants?.find(x => x.Id === variant);
        const showInvoiceDetails = pointOfSale === initValue.b2bFakturaPos;
        const gridStyle: React.CSSProperties = isCreateComplete || isCreatingOrder ? { opacity: 0.5, pointerEvents: "none", userSelect: "none" } : null;

        return (
            <Grid container spacing={2} style={gridStyle}>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel shrink>Produkt</InputLabel>
                        <ProductList
                            showArchived={false}
                            productTypes={["race"]}
                            onChange={handleProductChange}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel shrink>Variant</InputLabel>
                        <Select
                            value={variant ?? ""}
                            onChange={handleVariantChange}
                        >
                            {raceProduct && raceProduct.variants.map((v) => {
                                return <MenuItem key={v.Id} value={v.Id}>{v.Name}</MenuItem>
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel shrink>Startled (valfritt)</InputLabel>
                        <StartGroupSelector
                            key={raceProduct?.id}
                            productIds={raceProduct?.id ?? ""}
                            clearable
                            onChange={(ev) => {
                                setStartGroup(ev.target.value as string);
                            }}
                            onClear={() => {
                                setStartGroup(null);
                            }}
                            valueFormatter={(gr) => {
                                const remainingSellable = gr.numberOfSellablePlaces - gr.numberOfOccupiedPlaces - gr.numberOfReservedPlaces;
                                const remainingTotal = gr.maxParticipants - gr.numberOfOccupiedPlaces - gr.numberOfReservedPlaces; // Skippa efter dialog
                                return `${gr.name}, ${vasaloppetTimeGetterFormatter(gr.startTime)}, ${remainingTotal} lediga (${remainingSellable} publikt lediga)`;
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel shrink>Prisgrupp</InputLabel>
                        <Select
                            native
                            value={priceGroup?.id ?? ""}
                            onChange={(ev) => {
                                const match = selectedVariant?.priceGroups?.find(x => x.id === ev.target.value);
                                setPriceGroup(match ?? null);
                            }}
                        >
                            {!priceGroup && <option key="_" value=""></option>}
                            {selectedVariant?.priceGroups.map((pg) => {
                                return <option key={pg.id} value={pg.id}>{pg.Name}</option>
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        name="numItems"
                        InputLabelProps={{ shrink: true }}
                        value={numItems}
                        label="Antal"
                        fullWidth
                        type="number"
                        InputProps={{ inputProps: { min: 1 } }}
                        onChange={handleNumItemsChange}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        name="reservationName"
                        InputLabelProps={{ shrink: true }}
                        value={reservationName}
                        label="Namn på reservation (fritext)"
                        fullWidth
                        onChange={(e: any) => {
                            setReservationName(e.target.value);
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel shrink>POS</InputLabel>
                        <SelectPointOfSale
                            initialValue={initValue.b2bFakturaPos ?? ""}
                            onChange={handlePosChange}
                        />
                    </FormControl>
                </Grid>

                {showInvoiceDetails && <>
                    <Grid item xs={12}
                        style={{ background: theme.palette.primary.dark, color: theme.palette.primary.main }}
                    >
                        Specifika uppgifter för B2B Faktura
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel
                            control={
                                <Checkbox color="primary"
                                    name="invoiceLater"
                                    checked={invoiceLater}
                                    onChange={handleInvoiceLaterChange}
                                />
                            }
                            label="Faktureras senare"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth disabled={!invoiceLater}>
                            <InputLabel shrink>Datum att fakturera</InputLabel>
                            <Input
                                type="date"
                                value={invoiceDate ?? ""}
                                onChange={handleInvoiceDate}
                            />
                        </FormControl>
                    </Grid>
                </>}
            </Grid>
        );
    };

    return render();
}

export default CreateReservationDialog;
